import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Eye, MessageSquare, Building2, TrendingUp, Lock } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, Tooltip, CartesianGrid } from "recharts";

export default function AgencyStats() {
    const { user } = useAuth();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/me/stats").then((r) => setStats(r.data)).catch(() => setStats(null)).finally(() => setLoading(false));
    }, []);

    if (!user) return null;
    if (!user.plan_active) {
        return (
            <div data-testid="stats-locked" className="border border-white/10 p-10 bg-ink-900/40 text-center">
                <Lock size={28} className="text-gold mx-auto mb-4" />
                <div className="font-serif text-xl text-ink-50 mb-2">Statistiche riservate</div>
                <p className="text-sm text-ink-400 mb-6">Le statistiche sono incluse nei piani Premium e Agenzia.</p>
                <Link to="/prezzi" className="px-6 py-3 bg-gold text-black text-sm font-medium hover:bg-gold-soft transition-colors inline-block">Vedi i piani</Link>
            </div>
        );
    }
    if (loading || !stats) return <div className="text-ink-400">Caricamento statistiche...</div>;

    const isAgency = stats.summary.is_agency;

    return (
        <div data-testid="stats-section" className="space-y-10">
            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Kpi icon={<Building2 size={16} />} label="Annunci" value={stats.summary.total_listings} testid="kpi-listings" />
                <Kpi icon={<Eye size={16} />} label="Visualizzazioni" value={stats.summary.total_views} testid="kpi-views" />
                <Kpi icon={<MessageSquare size={16} />} label="Contatti ricevuti" value={stats.summary.total_contacts} testid="kpi-contacts" locked={!isAgency} />
                <Kpi icon={<TrendingUp size={16} />} label="Conv. rate" value={`${stats.summary.total_views ? ((stats.summary.total_contacts / stats.summary.total_views) * 100).toFixed(1) : 0}%`} testid="kpi-rate" locked={!isAgency} />
            </div>

            {/* Daily chart (agency only) */}
            {isAgency && (
                <div className="border border-white/10 p-6 bg-ink-900/40">
                    <div className="label-eyebrow mb-4">Contatti ultimi 30 giorni</div>
                    <div style={{ width: "100%", height: 220 }}>
                        <ResponsiveContainer>
                            <LineChart data={stats.daily_contacts}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#A1A1AA" }} tickFormatter={(v) => v.slice(-5)} />
                                <Tooltip contentStyle={{ background: "#0F0F0F", border: "1px solid #27272a", borderRadius: 0, fontSize: 12 }} labelStyle={{ color: "#D4AF6A" }} />
                                <Line type="monotone" dataKey="contacts" stroke="#D4AF6A" strokeWidth={2} dot={false} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}

            {/* Per listing */}
            {stats.per_listing.length > 0 && (
                <div className="border border-white/10 bg-ink-900/40">
                    <div className="px-6 pt-6 label-eyebrow">Performance per annuncio</div>
                    <div className="divide-y divide-white/5 mt-4">
                        {stats.per_listing.map((p) => (
                            <div key={p.id} data-testid={`stat-row-${p.id}`} className="px-6 py-4 grid grid-cols-12 gap-4 items-center text-sm">
                                <Link to={`/annunci/${p.id}`} className="col-span-7 text-ink-100 hover:text-gold truncate">
                                    {p.title}
                                    <span className="text-ink-400 ml-2 text-xs">· {p.city}</span>
                                </Link>
                                <div className="col-span-2 flex items-center gap-1.5 text-ink-100"><Eye size={13} className="text-gold" /> {p.views}</div>
                                <div className="col-span-2 flex items-center gap-1.5 text-ink-100"><MessageSquare size={13} className="text-gold" /> {isAgency ? p.contacts : "—"}</div>
                                <div className="col-span-1 text-right text-xs text-ink-400">
                                    {isAgency && p.views > 0 ? `${((p.contacts / p.views) * 100).toFixed(0)}%` : ""}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

const Kpi = ({ icon, label, value, testid, locked }) => (
    <div className="border border-white/10 p-5 bg-ink-900/40" data-testid={testid}>
        <div className="flex items-center gap-2 text-xs text-gold tracking-[0.2em] uppercase">
            {icon} {label}
        </div>
        <div className="font-serif text-3xl text-ink-50 mt-3">
            {locked ? <Lock size={20} className="text-ink-400 mt-2" /> : value}
        </div>
    </div>
);
